/**
 * Shared by Settings › Reset data and its tests: the permissions, the phrase
 * each kind of reset asks for, and what the API sends back.
 */

export const RESET_READ = 'sys.reset.read';
export const RESET_POST = 'sys.reset.post';
/** Only the Super Admin holds it; nobody else is ever told it exists. */
export const WIPE_POST = 'sys.fullreset.post';

export interface ResetAreaDef {
  id: string;
  label: string;
  group: string;
  description: string;
  needs: string[];
  needsWhy: string | null;
}

export type ResetMode = 'partial' | 'everything' | 'complete';

export interface ResetPreview {
  mode: ResetMode;
  areas: Array<{
    id: string;
    label: string;
    pulledIn: boolean;
    tables: Array<{ table: string; count: number }>;
    total: number;
  }>;
  pulledIn: string[];
  total: number;
  kept: string[];
  numbering: string;
}

export interface ResetOutcome {
  mode: ResetMode;
  totalRemoved: number;
  backupFile: string | null;
  backupWarning: string | null;
  signedOut: boolean;
}

export const GROUPS = ['Stock & items', 'Farms', 'HR', 'Other'];

export const PHRASES: Record<ResetMode, string> = {
  partial: 'CLEAR',
  everything: 'CLEAR EVERYTHING',
  complete: 'RESET EVERYTHING',
};

/**
 * The ticked areas plus everything they need, and for each added one the
 * first ticked area that pulled it in. The server works this out again for
 * itself; this is so the screen can show it before anything is sent.
 */
export function closeOver(
  ticked: ReadonlySet<string>,
  areas: ResetAreaDef[],
): { all: Set<string>; pulledBy: Map<string, string> } {
  const byId = new Map(areas.map((a) => [a.id, a]));
  const all = new Set<string>();
  const pulledBy = new Map<string, string>();
  const visit = (id: string, root: string) => {
    if (all.has(id)) return;
    all.add(id);
    if (id !== root && !ticked.has(id)) pulledBy.set(id, root);
    for (const n of byId.get(id)?.needs ?? []) visit(n, root);
  };
  for (const id of ticked) visit(id, id);
  return { all, pulledBy };
}
